import { useQuery } from "react-query";
import { getDataWithDotNet } from "../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../api/ajax-endpoints";
import { USE_MOCK } from "../appConstants";
import { IPaginatedResponse } from "../interfaces/response";

export type TParams = {
  pageNumber: number;
  size: number;
  startDate?: string;
  endDate?: string;
  searchValue?: string;
  dateCreated?: string;
  TierType: number;
};

export interface IBvnDetails {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  age: string;
  gender: string;
  birthCountry: string;
}

export interface INinDetails {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  age: string;
  gender: string;
  birthCountry: string;
}

export interface IGovernmentIdInfo {
  uploadPath: string;
  filename: string;
  dateUploaded: string;
  status: {
    id: number;
    name: string;
    description: string | null;
  };
  idType: string;
  comment: string;
}

export interface IPendingTierUpgradeRequest {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  accountNumber: string;
  email: string;
  dateCreated: string;
  gender: string;
  bvn: string;
  bvnDetails: IBvnDetails;
  kycRequestDate: string;
  transactionLimit: string;
  nin: string;
  ninDetails: INinDetails;
  isAddressVerified: boolean;
  isBvnVerified: boolean;
  isNinVerified: boolean;
  picturePath: string;
  governmentIdInfo: IGovernmentIdInfo;
  addressVerificationStatus: string;
  employmentDetails: {
    educationalQualification: {
      id: number;
      name: string;
      description: string | null;
    };
    employmentStatus: {
      id: number;
      name: string;
      description: string | null;
    };
    sourceOfIncome: {
      id: number;
      name: string;
      description: string | null;
    };
    employmentStartDate: string;
  };
  residentialDetails: {
    typeOfResidence: {
      id: number;
      name: string;
      description: string | null;
    };
    address: string;
    city: string;
    state: string;
    countryCode: string;
    status: string;
    addressLocationId: string;
    addressVerificationCertificatePath: string | null;
    googleMapUrl: string | null;
  };
  proofOfAddressInfo: {
    uploadPath: string;
    filename: string;
    dateUploaded: string;
    status: {
      id: number;
      name: string;
      description: string | null;
    };
    idType: string;
    comment: string;
  };
  addressVerificationInfo: {
    usage_types: string[];
    user: {
      id: string;
      first_name: string;
      last_name: string;
      phone: string;
      email: string;
    };
    metadata: {
      org_id: string;
      app_id: string;
      branch_id: string;
      user_id: string;
      app_user_id: string;
    };
    location: {
      ward: string;
      formatted_address: string;
      post_code: string;
      street_name: string;
      property_number: string;
      neighborhood: string | null;
      district: string | null;
      lga: string;
      city: string;
      state: string;
      country: string;
      country_code: string;
      geo_point: {
        lat: number;
        lon: number;
      };
      user_id: string;
      directions: string;
      id: string;
      url: string;
      plus_code: string;
      usage_types: string[] | null;
    };
    address_verification: any | null;
  };
}

export const getPendingTierUpgradeRequests = async ({
  pageNumber,
  size,
  startDate,
  endDate,
  searchValue,
  TierType,
  dateCreated,
}: TParams): Promise<IPaginatedResponse<IPendingTierUpgradeRequest[]>> => {
  if (USE_MOCK) {
    // Return mock data using setTimeout to simulate network delay
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          message: "Requests fetched successfully!",
          status: true,
          status_code: 200,
          page: 1,
          pageSize: 10,
          totalCount: 100,
          totalPages: 100,
          data: [
            {
              id: 1,
              firstName: "dabi",
              lastName: "dubai",
              phoneNumber: "0812707798",
              accountNumber: "123456677",
              email: "cacedy@nomail.com",
              dateCreated: "20-10-10",
              gender: "Male",
              bvn: "1234566779",
              bvnDetails: {
                firstName: "emeka",
                middleName: "onasanjo",
                lastName: "mandela",
                dateOfBirth: "20-10-10",
                age: "29",
                gender: "Male",
                birthCountry: "NG",
              },
              kycRequestDate: "20-10-10",
              transactionLimit: "2000000",
              nin: "1232344556",
              ninDetails: {
                firstName: "Cane",
                middleName: "Ahmed",
                lastName: "Abel",
                dateOfBirth: "20-10-10",
                age: "59",
                gender: "Prefer Not To Say",
                birthCountry: "NG",
              },
              isAddressVerified: false,
              isBvnVerified: false,
              isNinVerified: false,
              picturePath: "",
              governmentIdInfo: {
                uploadPath:
                  "documents/onboarding/1539/adone_v2_png_061020251053025084.png",
                filename: "adone_v2_png_061020251053025084.png",
                dateUploaded: "2025-10-06T10:53:02.65764",
                status: {
                  id: 1,
                  name: "Pending",
                  description: null,
                },
                idType: "International Passport",
                comment: "accepted niken",
              },
              addressVerificationStatus: "Verified",
              employmentDetails: {
                educationalQualification: {
                  id: 1,
                  name: "National Diploma",
                  description: null,
                },
                employmentStatus: {
                  id: 1,
                  name: "Employed Full-Time",
                  description: null,
                },
                sourceOfIncome: {
                  id: 1,
                  name: "Salary / Wages",
                  description: null,
                },
                employmentStartDate: "2025-10-05T00:00:00",
              },
              residentialDetails: {
                typeOfResidence: {
                  id: 1,
                  name: "Apartment/Flat",
                  description: null,
                },
                address:
                  "There We go, Unit 44, 60 Gaun Road, Ifo, Iguan, 112107, Ogun, Nigeria, Directions: Kanu road",
                city: "Iguan",
                state: "Ogun",
                countryCode: "NG",
                status: "Verified",
                addressLocationId: "68e63b19055e37b3dee18b8e",
                addressVerificationCertificatePath: null,
                googleMapUrl: null,
              },
              proofOfAddressInfo: {
                uploadPath:
                  "documents/onboarding/1553/screenshot_2025_10_09_14_53_56_618_com_whatsapp_png_091020250947556981.png",
                filename:
                  "screenshot_2025_10_09_14_53_56_618_com_whatsapp_png_091020250947556981.png",
                dateUploaded: "2025-10-09T21:47:55.766708",
                status: {
                  id: 1,
                  name: "Pending",
                  description: null,
                },
                idType: "Electricity Bill",
                comment: "Please upload a valid document",
              },
              addressVerificationInfo: {
                usage_types: ["digital_verification"],
                user: {
                  id: "68e63ae5290ce2893eb07f0a",
                  first_name: "Jesse",
                  last_name: "Rippin",
                  phone: "+2347036499064",
                  email: "jesserip@yopmail.com",
                },
                metadata: {
                  org_id: "67a601959df1cdedbf842d8c",
                  app_id: "67b2f7adc2fe3e521814f57c",
                  branch_id: "67b2f7b0c2fe3e521814f58f",
                  user_id: "68e63ae5290ce2893eb07f0a",
                  app_user_id: "1550",
                },
                location: {
                  ward: "Ajuwon/Akute",
                  formatted_address:
                    "There We go, Unit 44, 60 Gaun Road, Ifo, Iguan, 112107, Ogun, Nigeria, Directions: Kanu road",
                  post_code: "112107",
                  street_name: "Gaun Road",
                  property_number: "60",
                  neighborhood: null,
                  district: null,
                  lga: "Ifo",
                  city: "Iguan",
                  state: "Ogun",
                  country: "Nigeria",
                  country_code: "ng",
                  geo_point: {
                    lat: 6.704542944104731,
                    lon: 3.386314818476368,
                  },
                  user_id: "68e63ae5290ce2893eb07f0a",
                  directions: "Kanu road",
                  id: "68e63b19055e37b3dee18b8e",
                  url: "https://okhi.me/68e63b19055e37b3dee18b8e",
                  plus_code: "6FR5P93P+RG",
                  usage_types: null,
                },
                address_verification: null,
              },
            },
          ],
        });
      }, 5000);
    });
  }

  let queryParams = `?Page=${pageNumber}&PageSize=${size}`;
  if (startDate) {
    queryParams = `${queryParams}&startDate=${startDate}`;
  }

  if (endDate) {
    queryParams = `${queryParams}&endDate=${endDate}`;
  }

  if (dateCreated) {
    queryParams = `${queryParams}&dateCreated=${dateCreated}`;
  }

  if (TierType) {
    queryParams = `${queryParams}&TierType=${TierType}`;
  }

  if (searchValue) queryParams += `&searchValue=${searchValue}`;

  return await getDataWithDotNet(
    `${ajaxEndpoints.DB_PENDING_TIER_UPGRADE_REQUESTS}${queryParams}`,
  );
};

export const usePendingTierUpgradeRequests = (params: TParams) => {
  return useQuery<IPaginatedResponse<IPendingTierUpgradeRequest[]>, Error>(
    ["getPendingTierUpgradeRequests", params],
    () => getPendingTierUpgradeRequests(params),
    {
      refetchOnWindowFocus: false,
    },
  );
};
